import React, { useState, useEffect } from 'react';
import {
  Container,
  GridLegacy as Grid,
  Card,
  CardContent,
  Typography,
  Box,
  TextField,
  Button,
  Paper,
  Chip,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Divider,
  LinearProgress,
} from '@mui/material';
import {
  CalendarMonth as CalendarIcon,
  CheckCircle as CheckIcon,
  Person as PersonIcon,
  Groups as GroupsIcon,
  TrendingUp as TrendingUpIcon,
  Schedule as ScheduleIcon,
  Edit as EditIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import api from '../services/api';

interface User {
  id: number;
  username: string;
  role: string;
  is_master?: boolean;
  organizationId?: number;
}

interface WeeklyPlan {
  mondayHours: number;
  tuesdayHours: number;
  wednesdayHours: number;
  thursdayHours: number;
  fridayHours: number;
  saturdayHours: number;
  sundayHours: number;
  notes: string;
}

interface TeamPlan {
  id: number;
  username: string;
  sunday_hours: number;
  monday_hours: number;
  tuesday_hours: number;
  wednesday_hours: number;
  thursday_hours: number;
  friday_hours: number;
  saturday_hours: number;
}

type DayKey = 'sunday' | 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday';
type ViewMode = 'personal' | 'team';

const WeeklyPlanning: React.FC = () => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [weekStartDate, setWeekStartDate] = useState<string>('');
  const [plan, setPlan] = useState<WeeklyPlan>({
    mondayHours: 0,
    tuesdayHours: 0,
    wednesdayHours: 0,
    thursdayHours: 0,
    fridayHours: 0,
    saturdayHours: 0,
    sundayHours: 0,
    notes: ''
  });
  const [teamPlans, setTeamPlans] = useState<TeamPlan[]>([]);
  const [isManager, setIsManager] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [message, setMessage] = useState<{ text: string; type: string }>({ text: '', type: '' });
  const [viewMode, setViewMode] = useState<ViewMode>('personal');

  const days: DayKey[] = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
  const dayLabels: Record<DayKey, string> = {
    sunday: 'Sunday',
    monday: 'Monday',
    tuesday: 'Tuesday',
    wednesday: 'Wednesday',
    thursday: 'Thursday',
    friday: 'Friday',
    saturday: 'Saturday'
  };

  const dayColors: Record<DayKey, string> = {
    sunday: '#9c27b0',
    monday: '#2196F3',
    tuesday: '#00bcd4',
    wednesday: '#009688',
    thursday: '#4caf50',
    friday: '#ff9800',
    saturday: '#ff5722'
  };

  useEffect(() => {
    fetchCurrentUser();
    fetchWeekStartDate();
  }, []);

  useEffect(() => {
    if (weekStartDate && currentUser) {
      fetchCurrentPlan();
      if (isManager && viewMode === 'team') {
        fetchTeamPlans();
      }
    }
  }, [weekStartDate, currentUser, viewMode, isManager]);

  const fetchCurrentUser = async () => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const selectedOrg = JSON.parse(localStorage.getItem('selectedOrg') || '{}');
      
      const userWithOrg: User = {
        ...user,
        organizationId: selectedOrg?.id || user.organizationId
      };
      
      setCurrentUser(userWithOrg);
      setIsManager(user.role === 'manager' || user.role === 'superuser' || user.is_master || user.permission_group_id === 4);
    } catch (error) {
      console.error('Error fetching user:', error);
    }
  };

  const fetchWeekStartDate = async () => {
    try {
      const response = await api.getWeekStart();
      setWeekStartDate(response.weekStartDate);
    } catch (error) {
      console.error('Error fetching week start date:', error);
    }
  };

  const fetchCurrentPlan = async () => {
    try {
      setLoading(true);
      const response = await api.getCurrentWeeklyPlan();
      if (response.plan) {
        setPlan({
          mondayHours: response.plan.monday_hours || 0,
          tuesdayHours: response.plan.tuesday_hours || 0,
          wednesdayHours: response.plan.wednesday_hours || 0,
          thursdayHours: response.plan.thursday_hours || 0,
          fridayHours: response.plan.friday_hours || 0,
          saturdayHours: response.plan.saturday_hours || 0,
          sundayHours: response.plan.sunday_hours || 0,
          notes: response.plan.notes || ''
        });
      }
    } catch (error) {
      console.error('Error fetching current plan:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTeamPlans = async () => {
    try {
      if (!currentUser?.organizationId) return;
      const response = await api.getTeamWeeklyPlans(currentUser.organizationId);
      setTeamPlans(response.plans || []);
    } catch (error) {
      console.error('Error fetching team plans:', error);
    }
  };

  const handleHourChange = (day: DayKey, value: string) => {
    const hours = parseFloat(value) || 0;
    if (hours >= 0 && hours <= 24) {
      setPlan(prev => ({
        ...prev,
        [`${day}Hours`]: hours
      }));
    }
  };

  const calculateTotalHours = (): number => {
    return days.reduce((total, day) => {
      return total + (parseFloat(String(plan[`${day}Hours` as keyof WeeklyPlan])) || 0);
    }, 0);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setMessage({ text: '', type: '' });

    try {
      const totalHours = calculateTotalHours();
      
      await api.submitWeeklyPlan({
        organizationId: currentUser?.organizationId,
        weekStartDate: weekStartDate,
        mondayHours: plan.mondayHours,
        tuesdayHours: plan.tuesdayHours,
        wednesdayHours: plan.wednesdayHours,
        thursdayHours: plan.thursdayHours,
        fridayHours: plan.fridayHours,
        saturdayHours: plan.saturdayHours,
        sundayHours: plan.sundayHours,
        notes: plan.notes
      });

      setMessage({ 
        text: `Weekly plan saved successfully! Total: ${totalHours.toFixed(1)} hours`, 
        type: 'success' 
      });

      if (isManager && viewMode === 'team') {
        fetchTeamPlans();
      }
    } catch (error: any) {
      setMessage({ 
        text: 'Error saving plan: ' + (error.response?.data?.error || error.message), 
        type: 'error' 
      });
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  };

  const exportToCSV = () => {
    if (teamPlans.length === 0) {
      setMessage({ text: 'No team data to export', type: 'error' });
      return;
    }

    // CSV Headers
    const headers = ['Team Member', 'Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Total'];
    const rows = [headers];

    // Add each team member's data
    teamPlans.forEach((teamPlan) => {
      const memberTotal = days.reduce((sum, day) => 
        sum + (parseFloat(String(teamPlan[`${day}_hours`])) || 0), 0
      );

      const row = [
        teamPlan.username,
        (parseFloat(String(teamPlan.sunday_hours)) || 0).toFixed(1),
        (parseFloat(String(teamPlan.monday_hours)) || 0).toFixed(1),
        (parseFloat(String(teamPlan.tuesday_hours)) || 0).toFixed(1),
        (parseFloat(String(teamPlan.wednesday_hours)) || 0).toFixed(1),
        (parseFloat(String(teamPlan.thursday_hours)) || 0).toFixed(1),
        (parseFloat(String(teamPlan.friday_hours)) || 0).toFixed(1),
        (parseFloat(String(teamPlan.saturday_hours)) || 0).toFixed(1),
        memberTotal.toFixed(1)
      ];
      rows.push(row);
    });

    // Add empty row for separation
    rows.push([]);

    // Calculate team totals
    const teamTotalHours = teamPlans.reduce((total, member) => {
      return total + days.reduce((sum, day) => 
        sum + (parseFloat(String(member[`${day}_hours`])) || 0), 0
      );
    }, 0);
    
    const avgHoursPerMember = teamTotalHours / teamPlans.length;

    // Calculate daily totals
    const sundayTotal = teamPlans.reduce((sum, m) => sum + (parseFloat(String(m.sunday_hours)) || 0), 0);
    const mondayTotal = teamPlans.reduce((sum, m) => sum + (parseFloat(String(m.monday_hours)) || 0), 0);
    const tuesdayTotal = teamPlans.reduce((sum, m) => sum + (parseFloat(String(m.tuesday_hours)) || 0), 0);
    const wednesdayTotal = teamPlans.reduce((sum, m) => sum + (parseFloat(String(m.wednesday_hours)) || 0), 0);
    const thursdayTotal = teamPlans.reduce((sum, m) => sum + (parseFloat(String(m.thursday_hours)) || 0), 0);
    const fridayTotal = teamPlans.reduce((sum, m) => sum + (parseFloat(String(m.friday_hours)) || 0), 0);
    const saturdayTotal = teamPlans.reduce((sum, m) => sum + (parseFloat(String(m.saturday_hours)) || 0), 0);

    // Add team summary section
    rows.push(['TEAM SUMMARY']);
    rows.push([]);
    rows.push(['Total Team Hours', '', '', '', '', '', '', '', teamTotalHours.toFixed(1)]);
    rows.push(['Team Members', teamPlans.length.toString()]);
    rows.push(['Avg per Member', '', '', '', '', '', '', '', avgHoursPerMember.toFixed(1)]);
    rows.push([]);
    rows.push(['Daily Breakdown']);
    rows.push(['Sunday', sundayTotal.toFixed(1)]);
    rows.push(['Monday', mondayTotal.toFixed(1)]);
    rows.push(['Tuesday', tuesdayTotal.toFixed(1)]);
    rows.push(['Wednesday', wednesdayTotal.toFixed(1)]);
    rows.push(['Thursday', thursdayTotal.toFixed(1)]);
    rows.push(['Friday', fridayTotal.toFixed(1)]);
    rows.push(['Saturday', saturdayTotal.toFixed(1)]);

    // Convert to CSV string
    const csvContent = rows.map(row => row.join(',')).join('\n');

    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    const fileName = `team-weekly-plans-${weekStartDate ? weekStartDate.replace(/\//g, '-') : 'current'}.csv`;
    link.setAttribute('href', url);
    link.setAttribute('download', fileName);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    setMessage({ text: 'Team plans exported successfully!', type: 'success' });
  };

  const totalHours = calculateTotalHours();
  const isTargetHours = totalHours === 30;
  const progressPercent = (totalHours / 30) * 100;

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress size={60} />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Paper elevation={0} sx={{ p: 3, mb: 3, borderRadius: 3, bgcolor: '#F8F9FA' }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
          <Box display="flex" alignItems="center" gap={2}>
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: 2,
                bgcolor: '#3f51b5',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <CalendarIcon sx={{ fontSize: 32, color: 'white' }} />
            </Box>
            <Box>
              <Typography variant="h4" fontWeight="bold" sx={{ color: '#0C0C0C' }}>
                Weekly Hour Planning
              </Typography>
              <Typography variant="body2" sx={{ color: '#666', mt: 0.5 }}>
                Week of {weekStartDate ? formatDate(weekStartDate) : '...'}
              </Typography>
            </Box>
          </Box>

          {isManager && (
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={(_, newMode: ViewMode | null) => newMode && setViewMode(newMode)}
              sx={{ bgcolor: 'white' }}
            >
              <ToggleButton value="personal" sx={{ px: 3 }}>
                <PersonIcon sx={{ mr: 1 }} />
                My Plan
              </ToggleButton>
              <ToggleButton value="team" sx={{ px: 3 }}>
                <GroupsIcon sx={{ mr: 1 }} />
                Team View
              </ToggleButton>
            </ToggleButtonGroup>
          )}
        </Box>
      </Paper>

      {/* Success/Error Message */}
      {message.text && (
        <Alert 
          severity={message.type === 'success' ? 'success' : 'error'} 
          onClose={() => setMessage({ text: '', type: '' })}
          sx={{ mb: 3 }}
        >
          {message.text}
        </Alert>
      )}

      {viewMode === 'personal' ? (
        <Grid container spacing={3}>
          {/* Hour Tracker Card */}
          <Grid item xs={12} lg={4}>
            <Card elevation={2} sx={{ height: '100%', borderRadius: 3 }}>
              <CardContent sx={{ p: 3 }}>
                <Box display="flex" alignItems="center" gap={1} mb={2}>
                  <TrendingUpIcon sx={{ color: '#3f51b5' }} />
                  <Typography variant="h6" fontWeight="600">
                    Hour Summary
                  </Typography>
                </Box>

                <Box textAlign="center" py={3}>
                  <Typography 
                    variant="h2" 
                    fontWeight="bold"
                    sx={{ 
                      color: isTargetHours ? '#4caf50' : totalHours > 30 ? '#f44336' : '#2196F3',
                      mb: 1 
                    }}
                  >
                    {totalHours.toFixed(1)}
                    {isTargetHours && (
                      <CheckIcon sx={{ fontSize: 48, ml: 1, verticalAlign: 'middle', color: '#4caf50' }} />
                    )}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    of 30 hours target
                  </Typography>

                  <Box sx={{ mt: 3, mb: 2 }}>
                    <LinearProgress 
                      variant="determinate" 
                      value={Math.min(progressPercent, 100)} 
                      sx={{ 
                        height: 10, 
                        borderRadius: 5,
                        bgcolor: '#e0e0e0',
                        '& .MuiLinearProgress-bar': {
                          bgcolor: isTargetHours ? '#4caf50' : totalHours > 30 ? '#f44336' : '#2196F3'
                        }
                      }}
                    />
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    {progressPercent.toFixed(0)}% of target
                  </Typography>
                </Box>

                <Divider sx={{ my: 2 }} />

                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Daily Breakdown
                  </Typography>
                  {days.map((day) => {
                    const hours = parseFloat(String(plan[`${day}Hours` as keyof WeeklyPlan])) || 0;
                    return hours > 0 ? (
                      <Box key={day} display="flex" justifyContent="space-between" alignItems="center" py={0.5}>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Box
                            sx={{
                              width: 8,
                              height: 8,
                              borderRadius: '50%',
                              bgcolor: dayColors[day],
                            }}
                          />
                          <Typography variant="body2">{dayLabels[day]}</Typography>
                        </Box>
                        <Chip 
                          label={`${hours.toFixed(1)}h`} 
                          size="small" 
                          sx={{ 
                            bgcolor: `${dayColors[day]}20`,
                            color: dayColors[day],
                            fontWeight: 600
                          }}
                        />
                      </Box>
                    ) : null;
                  })}
                </Box>

                {!isTargetHours && totalHours > 0 && (
                  <Alert 
                    severity={totalHours > 30 ? 'warning' : 'info'} 
                    sx={{ mt: 2 }}
                    icon={<ScheduleIcon />}
                  >
                    <Typography variant="caption">
                      {totalHours > 30 
                        ? `You're planning ${(totalHours - 30).toFixed(1)} hours over the 30-hour target.`
                        : `Add ${(30 - totalHours).toFixed(1)} more hours to reach the 30-hour target.`
                      }
                    </Typography>
                  </Alert>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Planning Form */}
          <Grid item xs={12} lg={8}>
            <Card elevation={2} sx={{ borderRadius: 3 }}>
              <CardContent sx={{ p: 3 }}>
                <Box display="flex" alignItems="center" gap={1} mb={3}>
                  <EditIcon sx={{ color: '#3f51b5' }} />
                  <Typography variant="h6" fontWeight="600">
                    Plan Your Week - Put Committed Hours for Each Day and Save
                  </Typography>
                </Box>

                <form onSubmit={handleSubmit}>
                  <Grid container spacing={2}>
                    {days.map((day) => (
                      <Grid item xs={12} sm={6} md={4} lg={3} key={day}>
                        <Paper 
                          elevation={0}
                          sx={{ 
                            p: 2, 
                            borderRadius: 2,
                            border: '2px solid',
                            borderColor: Number(plan[`${day}Hours` as keyof WeeklyPlan]) > 0 ? dayColors[day] : '#e0e0e0',
                            bgcolor: Number(plan[`${day}Hours` as keyof WeeklyPlan]) > 0 ? `${dayColors[day]}08` : 'transparent',
                            transition: 'all 0.2s'
                          }}
                        >
                          <Typography 
                            variant="subtitle2" 
                            fontWeight="600"
                            sx={{ mb: 1, color: Number(plan[`${day}Hours` as keyof WeeklyPlan]) > 0 ? dayColors[day] : '#666' }}
                          >
                            {dayLabels[day]}
                          </Typography>
                          <TextField
                            type="number"
                            fullWidth
                            size="small"
                            value={plan[`${day}Hours` as keyof WeeklyPlan]}
                            onChange={(e) => handleHourChange(day, e.target.value)}
                            onWheel={(e) => (e.target as HTMLInputElement).blur()}
                            inputProps={{ 
                              min: 0, 
                              max: 24, 
                              step: 0.5,
                              style: { textAlign: 'center', fontWeight: 600, fontSize: '1.1rem' }
                            }}
                            placeholder="0"
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                bgcolor: 'white',
                              }
                            }}
                          />
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>

                  <Box mt={3}>
                    <TextField
                      fullWidth
                      multiline
                      rows={3}
                      label="Notes (Optional)"
                      value={plan.notes}
                      onChange={(e) => setPlan(prev => ({ ...prev, notes: e.target.value }))}
                      placeholder="Add any notes about your weekly schedule..."
                      sx={{ mb: 3 }}
                    />
                  </Box>

                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    fullWidth
                    disabled={saving}
                    startIcon={saving ? <CircularProgress size={20} /> : <CheckIcon />}
                    sx={{
                      py: 1.5,
                      bgcolor: '#3f51b5',
                      '&:hover': { bgcolor: '#303f9f' },
                      textTransform: 'none',
                      fontSize: '1rem',
                      fontWeight: 600
                    }}
                  >
                    {saving ? 'Saving...' : 'Save Weekly Plan'}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      ) : (
        <Grid container spacing={3}>
          {/* Team Hour Summary Card */}
          <Grid item xs={12} lg={4}>
            <Card elevation={2} sx={{ height: '100%', borderRadius: 3 }}>
              <CardContent sx={{ p: 3 }}>
                <Box display="flex" alignItems="center" gap={1} mb={2}>
                  <GroupsIcon sx={{ color: '#3f51b5' }} />
                  <Typography variant="h6" fontWeight="600">
                    Team Summary
                  </Typography>
                </Box>

                {teamPlans.length === 0 ? (
                  <Box textAlign="center" py={3}>
                    <Typography variant="body2" color="text.secondary">
                      No team data yet
                    </Typography>
                  </Box>
                ) : (
                  <>
                    <Box textAlign="center" py={3}>
                      {(() => {
                        const teamTotalHours = teamPlans.reduce((total, member) => {
                          return total + days.reduce((sum, day) => 
                            sum + parseFloat(String(member[`${day}_hours`])) || 0, 0
                          );
                        }, 0);
                        const avgHoursPerMember = teamTotalHours / teamPlans.length;

                        return (
                          <>
                            <Typography 
                              variant="h2" 
                              fontWeight="bold"
                              sx={{ color: '#2196F3', mb: 1 }}
                            >
                              {teamTotalHours.toFixed(1)}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                              total team hours committed
                            </Typography>
                            <Chip 
                              label={`${teamPlans.length} team members`}
                              size="small"
                              sx={{ mt: 1, bgcolor: '#e3f2fd', color: '#1976d2', fontWeight: 600 }}
                            />
                            <Box sx={{ mt: 2 }}>
                              <Typography variant="caption" color="text.secondary">
                                Avg per member: {avgHoursPerMember.toFixed(1)} hrs
                              </Typography>
                            </Box>
                          </>
                        );
                      })()}
                    </Box>

                    <Divider sx={{ my: 2 }} />

                    <Box>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Daily Breakdown (Team Total)
                      </Typography>
                      {days.map((day) => {
                        const dayTotal = teamPlans.reduce((sum, member) => 
                          sum + (parseFloat(String(member[`${day}_hours`])) || 0), 0
                        );
                        return dayTotal > 0 ? (
                          <Box key={day} display="flex" justifyContent="space-between" alignItems="center" py={0.5}>
                            <Box display="flex" alignItems="center" gap={1}>
                              <Box
                                sx={{
                                  width: 8,
                                  height: 8,
                                  borderRadius: '50%',
                                  bgcolor: dayColors[day],
                                }}
                              />
                              <Typography variant="body2">{dayLabels[day]}</Typography>
                            </Box>
                            <Chip 
                              label={`${dayTotal.toFixed(1)}h`} 
                              size="small" 
                              sx={{ 
                                bgcolor: `${dayColors[day]}20`,
                                color: dayColors[day],
                                fontWeight: 600
                              }}
                            />
                          </Box>
                        ) : null;
                      })}
                    </Box>
                  </>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Team Plans Table */}
          <Grid item xs={12} lg={8}>
            <Card elevation={2} sx={{ borderRadius: 3 }}>
              <CardContent sx={{ p: 3 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <GroupsIcon sx={{ color: '#3f51b5' }} />
                    <Typography variant="h6" fontWeight="600">
                      Team Weekly Plans
                    </Typography>
                  </Box>
                  {teamPlans.length > 0 && (
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<DownloadIcon />}
                      onClick={exportToCSV}
                      sx={{
                        bgcolor: '#4caf50',
                        '&:hover': { bgcolor: '#45a049' },
                        textTransform: 'none',
                        fontWeight: 600
                      }}
                    >
                      Export CSV
                    </Button>
                  )}
                </Box>

                {teamPlans.length === 0 ? (
                  <Box textAlign="center" py={8}>
                    <CalendarIcon sx={{ fontSize: 64, color: '#e0e0e0', mb: 2 }} />
                    <Typography variant="h6" color="text.secondary">
                      No team plans submitted yet
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      Team members will appear here once they submit their weekly plans
                    </Typography>
                  </Box>
                ) : (
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                          <TableCell sx={{ fontWeight: 600 }}>Team Member</TableCell>
                          {days.map(day => (
                            <TableCell key={day} align="center" sx={{ fontWeight: 600, color: dayColors[day] }}>
                              {dayLabels[day].substring(0, 3)}
                            </TableCell>
                          ))}
                          <TableCell align="center" sx={{ fontWeight: 600 }}>Total</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {teamPlans.map((teamPlan) => {
                          const memberTotal = days.reduce((sum, day) => 
                            sum + (parseFloat(String(teamPlan[`${day}_hours`])) || 0), 0
                          );

                          return (
                            <TableRow key={teamPlan.id} hover>
                              <TableCell sx={{ fontWeight: 500 }}>{teamPlan.username}</TableCell>
                              {days.map(day => {
                                const hours = parseFloat(String(teamPlan[`${day}_hours`])) || 0;
                                return (
                                  <TableCell key={day} align="center">
                                    {hours > 0 ? (
                                      <Chip 
                                        label={hours.toFixed(1)}
                                        size="small"
                                        sx={{ 
                                          bgcolor: `${dayColors[day]}15`,
                                          color: dayColors[day],
                                          fontWeight: 600,
                                          minWidth: 45
                                        }}
                                      />
                                    ) : (
                                      <Typography variant="body2" color="text.disabled">-</Typography>
                                    )}
                                  </TableCell>
                                );
                              })}
                              <TableCell align="center">
                                <Chip
                                  label={memberTotal.toFixed(1)}
                                  sx={{
                                    bgcolor: memberTotal === 30 ? '#4caf5020' : '#e0e0e0',
                                    color: memberTotal === 30 ? '#4caf50' : '#666',
                                    fontWeight: 700,
                                    minWidth: 60
                                  }}
                                  icon={memberTotal === 30 ? <CheckIcon /> : undefined}
                                />
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}
    </Container>
  );
};

export default WeeklyPlanning;
